import React, { useState } from 'react';
import Alert from '@material-ui/lab/Alert';
import { IntlShape, injectIntl } from 'react-intl';
import { createStructuredSelector } from 'reselect';
import { get, isEmpty, uniq, without } from 'lodash';
import { connect } from 'react-redux';
import styled from 'styled-components';

import messages from 'containers/PreOpeningPage/messages';
import { translate } from 'containers/services/translate';
import ClaimDeclaration, {
  claimDeclarationValidator,
} from 'containers/PreOpeningPage/ClaimDeclaration';
import NavigationButtons from 'components/navigationButtons';
import ClaimInformation, {
  claimInformationValidator,
} from 'containers/PreOpeningPage/ClaimInformation';
import OpposingParty from 'containers/PreOpeningPage/OpposingParty/OpposingParty';
import {
  fraudInfoValidator,
  opposingPartyValidator,
} from 'containers/PreOpeningPage/OpposingParty/utils';
import ClaimCircumstances, {
  claimCircumstancesValidator,
} from 'containers/PreOpeningPage/ClaimCircumstances';
import { requestGetDahirAmount } from 'containers/LawyerLegalProceedingsPage/actions';
import VictimCard from 'containers/PreOpeningPage/VictimCard';
import { victimsValidator } from 'containers/PreOpeningPage/utils';
import { PAGES } from 'routes';
import routesHelper from 'containers/services/routes-helper';
import { makeSelectListings } from 'containers/AppContainer/selectors';
import { Listings } from 'containers/AppContainer/typings';
import { setClaim } from 'containers/PreOpeningPage/actions';
import { makeSelecClaimToPreopen } from 'containers/PreOpeningPage/selectors';
import {
  makeSelectSearchClaims,
  makeSelectedClaims,
} from 'containers/IdentificationPage/selectors';
import Stepper from 'containers/AppContainer/Stepper';
import {
  extractOpposingParties,
  formatClaimToPreOpen,
  checkTotalVictims,
} from 'containers/PreOpeningPage/utils';
import { resolveSagisClaim } from 'helpers/utils';

import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { makeSelectCorporealLawyers } from 'containers/RecapManagerPage/selectors';
import userPrivileges, {
  PRIVILEGES,
} from 'containers/services/security/user-privileges';
import {
  formsAreValidated,
  listOfFormsAreValidated,
} from 'containers/services/validateForm';
import { INJURY_POST_DECEASED_SAGIS } from 'containers/PreOpeningPage/constants';
import { makeSelectDahirAmounts } from 'containers/LawyerLegalProceedingsPage/selectors';
import { CLAIM } from 'containers/data-structure';
import { VictimType } from 'typings/globalTypes';
const claimFormValidator = (
  claimToPreopen: any,
  isCourtBasicManagement?: boolean,
) => ({
  claimCircumstances: claimCircumstancesValidator(get(claimToPreopen, 'claim')),
  claimInformations: claimInformationValidator(claimToPreopen),
  claimDeclaration: claimDeclarationValidator(get(claimToPreopen, 'driver')),
  victims: victimsValidator(
    get(claimToPreopen, 'victims'),
    isCourtBasicManagement,
  ),
  opposingParty: opposingPartyValidator(get(claimToPreopen, 'opposingParty')),
  fraudInfo: fraudInfoValidator(get(claimToPreopen, 'fraudInfo')),
});

export const globalValidator = { claimFormValidator };

export interface PreOpeningManagerPageProps {
  intl: IntlShape;
  listings: Listings;
  setClaim: Function;
  storedClaimToPreopen: any;
  policyInfo: { sagis: {}; lossDate: Date };
  selectedClaim: any;
  corporealLawyers: any;
  location?: any;
  requestGetDahirAmount: Function;
  dahirAmount: any;
}

const StyledDiv = styled.div`
  height: 24px;
  text-align: left;
  font-family: Roboto;
  letter-spacing: 0;
  color: #384164;
  opacity: 1;
  font-size: 20px;
  margin-top: 50px;
  margin-bottom: 40px;
  font-weight: 100;
`;
export const PreOpeningManagerPage: React.FC<PreOpeningManagerPageProps> = (
  props,
): JSX.Element => {
  const [claimToPreopen, setClaimToPreopen] = useState({
    claim: { ...get(props.storedClaimToPreopen, 'claim', {}) },
    driver: { ...get(props.storedClaimToPreopen, 'driver', {}) },
    vehicule: get(props.storedClaimToPreopen, 'vehicule', []),
    victims: get(props.storedClaimToPreopen, 'victims', []),
    documents: get(props.storedClaimToPreopen, 'documents', []),
    opposingParty: {
      hasMaterialDammage: null,
      vehicules: [],
    },
  });
  React.useEffect(() => {
    if (isEmpty(get(props.storedClaimToPreopen, 'claim', {}))) {
      const claimToPreOpen = formatClaimToPreOpen(
        get(props, 'location.state.sagisInfoClaim', {}),
        props.listings,
        props.dahirAmount,
      );
      setClaimToPreopen({ ...claimToPreOpen });
    }
  }, [!get(props, 'location.state.sagisInfoClaim', {}), props.dahirAmount]);
  React.useEffect(() => {
    if (isEmpty(get(props.storedClaimToPreopen, 'claim'))) {
      props.requestGetDahirAmount(
        [
          ...without(
            uniq(
              get(props, 'location.state.sagisInfoClaim.victims', [])
                ?.filter(
                  (v: any) =>
                    String(v.injuryCode) !== INJURY_POST_DECEASED_SAGIS,
                )
                .map((v: any) => v.VictimKey),
            ),
            null,
            '',
          ),
        ],
        [],
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [claimValidation, setClaimValidation] = React.useState({});

  const handleChange = (claimToPreopen: any) => {
    setClaimToPreopen(claimToPreopen);
  };

  const [isOpenAlert, setIsOpenAlert] = React.useState(false);

  const handleNextPageClick = () => {
    props.setClaim(claimToPreopen);
    if (
      !checkTotalVictims(
        // @ts-ignore
        parseInt(claimToPreopen.claim[CLAIM.TOTAL_INJURED_VICTIMS]),
        // @ts-ignore
        parseInt(claimToPreopen.claim[CLAIM.TOTAL_DECEASED_VICTIMS]),
        get(claimToPreopen, 'victims', []),
      )
    ) {
      return setIsOpenAlert(true);
    }
    const validator = globalValidator.claimFormValidator(
      claimToPreopen,
      featureService.hasFeature(FEATURES.BASIC_MANAGEMENT_REGULATION),
    );

    let expectedValidatorVictims = get(validator, 'victims', []);
    expectedValidatorVictims = expectedValidatorVictims.map(
      (victim: VictimType) => {
        const expectedVictim = { ...victim };
        delete (expectedVictim as any).id;
        return expectedVictim;
      },
    );
    const expectedValidator = {
      claimCircumstances: get(validator, 'claimCircumstances'),
      claimInformations: get(validator, 'claimInformations'),
      claimDeclaration: get(validator, 'claimDeclaration'),
    };
    setClaimValidation(validator);
    if (
      formsAreValidated(expectedValidator) &&
      listOfFormsAreValidated(expectedValidatorVictims)
    ) {
      routesHelper.pushTo(PAGES.PREOPENING_MANAGER_UPLOAD_DOCUMENTS, {
        isFromSagis: true,
        judicialProcedureId: get(
          claimToPreopen,
          'claim.judicialProcedureId',
          null,
        ),
        sagisInfoClaim: get(props, 'location.state.sagisInfoClaim'),
      })();
    }
  };

  return (
    <div>
      <Stepper
        isCourtManager={userPrivileges.userHasPrivilege(
          PRIVILEGES.ISCOURTMANAGER,
        )}
        isBasicManagement={get(props, 'location.state.isFromSagis')}
      />
      <StyledDiv>
        {translate({ ...messages.preOpeningPageTitle }, props)}
      </StyledDiv>
      {isOpenAlert && (
        <Alert
          severity="error"
          style={{ marginBottom: '16px' }}
          onClose={() => {
            setIsOpenAlert(false);
          }}
        >
          {translate(messages.preOpeningPageAlertError, props)}
        </Alert>
      )}
      <ClaimDeclaration
        listings={props.listings}
        updateFilde={handleChange}
        claimToPreopen={claimToPreopen}
        storedClaimToPreopen={props.storedClaimToPreopen}
        policyInfo={props.policyInfo}
        claimDeclarationValidation={get(
          claimValidation,
          'claimDeclaration',
          {},
        )}
        isRecapPage={false}
        isReadOnly={false}
        isManager={false}
        selectedClaim={props.selectedClaim}
        isBasicManagement={get(props, 'location.state.isFromSagis')}
        location={get(props, 'location')}
      />
      <ClaimInformation
        isRecapPage={false}
        isReadOnly={false}
        isManager={false}
        policyInfo={props.policyInfo}
        updateFilde={handleChange}
        listings={props.listings}
        claimToPreopen={claimToPreopen}
        isCourtManagerBasicManagement={featureService.hasFeature(
          FEATURES.BASIC_MANAGEMENT_REGULATION,
        )}
        storedClaimToPreopen={props.storedClaimToPreopen}
        claimInformationsValidation={get(
          claimValidation,
          'claimInformations',
          {},
        )}
        stakeholders={[]}
        isBasicManagement={get(props, 'location.state.isFromSagis')}
        isCourtManager={userPrivileges.userHasPrivilege(
          PRIVILEGES.ISCOURTMANAGER,
        )}
        location={get(props, 'location')}
      />
      {featureService.hasFeature(FEATURES.OPPOSING_PARTY) &&
        !isEmpty(get(claimToPreopen, 'opposingParty.vehicules')) && (
          <OpposingParty
            listings={props.listings}
            setClaimToPreopen={setClaimToPreopen}
            claimToPreopen={claimToPreopen}
            opposingPartyValidation={get(claimValidation, 'opposingParty', {})}
            sagisOpposingParties={extractOpposingParties(
              get(resolveSagisClaim(props.policyInfo), 'offeredRisks', []),
            )}
            isCourtManagerBasicManagement={featureService.hasFeature(
              FEATURES.BASIC_MANAGEMENT_REGULATION,
            )}
            location={get(props, 'location')}
          />
        )}
      <ClaimCircumstances
        isRecapPage={false}
        isReadOnly={false}
        isManager={false}
        updateFilde={handleChange}
        listings={props.listings}
        claimToPreopen={claimToPreopen}
        storedClaimToPreopen={props.storedClaimToPreopen}
        claimCircumstancesValidation={get(
          claimValidation,
          'claimCircumstances',
          {},
        )}
        claimCircumstancesSagis={{}}
        isBasicManagement={get(props, 'location.state.isFromSagis')}
        location={get(props, 'location')}
      />
      <VictimCard
        isRecapPage={false}
        isReadOnly={false}
        isManager={false}
        claimToPreopen={claimToPreopen}
        listings={props.listings}
        updateFilde={handleChange}
        storedClaimToPreopen={props.storedClaimToPreopen}
        victimsValidation={get(claimValidation, 'victims', [])}
        policyInfo={props.policyInfo}
        isBasicManagement={get(props, 'location.state.isFromSagis')}
        isCourtManagerBasicManagement={featureService.hasFeature(
          FEATURES.BASIC_MANAGEMENT_REGULATION,
        )}
        location={get(props, 'location')}
      />
      <NavigationButtons
        forwardClick={handleNextPageClick}
        onPreviousClick={routesHelper.pushTo(PAGES.COURT_MANAGER_HOME)}
      />
    </div>
  );
};

const mapDispatchToProps = { setClaim, requestGetDahirAmount };

const mapStateToProps = createStructuredSelector({
  listings: makeSelectListings(),
  storedClaimToPreopen: makeSelecClaimToPreopen(),
  policyInfo: makeSelectSearchClaims(),
  selectedClaim: makeSelectedClaims(),
  corporealLawyers: makeSelectCorporealLawyers(),
  dahirAmount: makeSelectDahirAmounts(),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(PreOpeningManagerPage));
